import { AppRouteMeta } from '@sennder/shell-utilities'

import {
  restrictForCharteringOfficeUsers,
  redirectToSingleViewAP,
  redirectToTrip,
  redirectToCustomerOrder,
  redirectToDefaultPage,
  canAcessTransportAnalysis,
  checkSpotAutobiddingAccess,
  checkExperimentsAccess,
  restrictToDev,
} from './route-guards'
import {
  ApplicationTypes,
  getRenderer as getRegistryRenderer,
  MFRepositories,
} from '@sennder/microfrontend-registry'
import { RouteRecordRedirectOption } from 'vue-router'

/**
 * Unique application route
 */
export type AppRoute = keyof typeof routes

export const getRenderer = <
  TRepo extends MFRepositories,
  TApp extends keyof ApplicationTypes[TRepo],
>(
  Repo: TRepo,
  App: TApp
) =>
  getRegistryRenderer<TRepo, TApp, unknown>(Repo, App, {
    integrator: 'SennTMS',
  })

const octopus = getRenderer(MFRepositories.OctopusCore, 'page')
const marketplace = getRenderer(MFRepositories.Marketplace, 'page')

/**
 * Route Redirects for legacy routes or route aliases. Ordered alphabetically by path.
 */
export const redirects = {
  '/dashboard': redirectToDefaultPage,
  '/dashboard/finance': redirectToSingleViewAP,
  '/experiments/tendering': '/tenders/new',
  '/experiments/tendering-list': '/tenders',
  '/finance/ordering/:orderGroupId(\\d+):lettegetRenderer([A-Z]+)':
    redirectToTrip,
  '/finance/ordering/:orderGroupId(\\d+):letter([A-Z]+)/:financeView':
    redirectToTrip,
  '/ordering/:orderGroupId(\\d+):letter([A-Z]+)': redirectToTrip,
  '/shipment/:id': redirectToCustomerOrder,
} as const satisfies {
  [TRoute: string]: RouteRecordRedirectOption
}

/**
 * All routes of the application, ordered alphabetically by path.
 * Each route has navigation guards, analytical and logging contexts.
 */
export const routes = {
  '/blocklisting-dashboard': {
    name: 'blocklisting-dashboard',
    renderer: getRenderer(MFRepositories.Vetting, 'BlocklistingDashboard'),
    context: {
      analytics: {
        module: 'vetting_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'vetting',
        module: 'vetting_micro_frontends',
      },
    },
  },
  '/blocklisting/:blocklistingId': {
    name: 'blocklisting-review',
    renderer: getRenderer(MFRepositories.Vetting, 'VettingReview'),
    context: {
      analytics: {
        module: 'vetting_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'vetting',
        module: 'vetting_micro_frontends',
      },
    },
  },
  '/contract-management': {
    name: 'contract-management',
    renderer: getRenderer(MFRepositories.ContractManagementMF, 'page'),
    context: {
      analytics: {
        module: 'contract-management',
        submodule: '',
      },
      logger: {
        codeOwners: 'tendering',
        module: 'contract-management',
      },
    },
  },
  '/suppliers': {
    name: 'suppliers-dashboard',
    renderer: getRenderer(MFRepositories.SupplierData, 'SuppliersDashboard'),
    context: {
      analytics: {
        module: 'supplier_data_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'supplier_data_micro_frontends',
      },
    },
  },
  '/suppliers/vendors/create': {
    name: 'vendor-create',
    renderer: getRenderer(MFRepositories.SupplierData, 'VendorCreate'),
    context: {
      analytics: {
        module: 'supplier_data_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'supplier_data_micro_frontends',
      },
    },
  },
  '/suppliers/vendors/:id': {
    name: 'vendor-profile',
    renderer: getRenderer(MFRepositories.SupplierData, 'VendorProfile'),
    context: {
      analytics: {
        module: 'supplier_data_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'supplier_data_micro_frontends',
      },
    },
  },
  '/suppliers/document/:entity/:id': {
    name: 'suppliers-document-url-resolver',
    renderer: getRenderer(
      MFRepositories.SupplierData,
      'SuppliersDocumentURLResolver'
    ),
    context: {
      analytics: {
        module: 'supplier_data_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'supplier_data_micro_frontends',
      },
    },
  },
  '/carriers': {
    name: 'carrier-search',
    renderer: getRenderer(MFRepositories.CarrierSearch, 'page'),
    context: {
      analytics: {
        module: 'carrier-search-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'carrier-search-mf-component',
      },
    },
  },
  '/carriers/:carrierId': {
    name: 'carrier-profile',
    renderer: getRenderer(MFRepositories.CarrierProfile, 'page'),
    context: {
      analytics: {
        module: 'carrier-profile-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-data',
        module: 'carrier-profile-mf-component',
      },
    },
  },
  '/consolidation-cargo/:id': {
    name: 'consolidation-cargo',
    renderer: getRenderer(MFRepositories.Shipment, 'shipment'),
    context: {
      analytics: {
        module: 'shipment-mf-component',
        submodule: 'consolidation-cargo',
      },
      logger: {
        codeOwners: 'ingestion',
        module: 'shipment',
      },
    },
  },
  '/facilities': {
    name: 'facility-management',
    renderer: getRenderer(
      MFRepositories.FacilityManagement,
      'FacilityManagementPage'
    ),
    context: {
      analytics: {
        module: 'facility-management-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'facility-management',
        module: 'facility-management-mf-component',
      },
    },
  },
  '/analysis': {
    name: 'analysis-board',
    renderer: getRenderer(MFRepositories.Analysis, 'page'),
    context: {
      analytics: {
        module: 'analysis-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'analysis-board',
        module: 'analysis-mf-component',
      },
    },
  },

  '/login': {
    name: 'login',
    renderer: getRenderer(MFRepositories.InternalAuthV2, 'login'),
    context: {
      analytics: {
        module: 'internal-auth-mf-component-v2',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-core',
        module: 'internal-auth-mf-component-v2',
      },
    },
    fullscreenLayout: true,
    public: true,
  },
  '/order': {
    name: 'ordering-legacy-redirect',
    renderer: octopus,
    context: {
      analytics: {
        module: 'octopus-core-mf-component',
        submodule: 'ordering-legacy-redirect',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'ordering-legacy-redirect',
      },
    },
  },
  '/planner': {
    name: 'planner',
    renderer: getRenderer(MFRepositories.Planner, 'page'),
    context: {
      analytics: {
        module: 'planner',
        submodule: '',
      },
      logger: {
        codeOwners: 'asset-planning',
        module: 'planner-mf-component',
      },
    },
    guards: [restrictForCharteringOfficeUsers],
  },
  '/contracts': {
    name: 'contracts',
    renderer: getRenderer(MFRepositories.Chartering, 'page'),
    context: {
      analytics: {
        module: 'contracts',
        submodule: '',
      },
      logger: {
        codeOwners: 'asset-planning',
        module: 'chartering-contracts',
      },
    },
    guards: [restrictForCharteringOfficeUsers],
  },
  '/transport-analysis': {
    name: 'transport-analysis',
    renderer: getRenderer(MFRepositories.TransportAnalysis, 'page'),
    context: {
      analytics: {
        module: 'transport-analysis-ui',
        submodule: '',
      },
      logger: {
        codeOwners: 'Global Commercial',
        module: 'transport-analysis-ui',
      },
    },
    guards: [canAcessTransportAnalysis],
  },
  '/marketplace': {
    name: 'marketplace',
    renderer: marketplace,
    context: {
      analytics: {
        module: 'marketplace',
        submodule: '',
      },
      logger: {
        codeOwners: 'pod-discovery',
        module: 'marketplace-mf-component',
      },
    },
    guards: [restrictForCharteringOfficeUsers],
  },
  '/order-details/:idForStaff': {
    name: 'order-details',
    renderer: marketplace,
    context: {
      analytics: {
        module: 'order-details',
        submodule: '',
      },
      logger: {
        module: 'marketplace-mf-component',
        codeOwners: 'pod-discovery',
      },
    },
    guards: [restrictForCharteringOfficeUsers],
  },
  '/senn-teams': {
    name: 'senn-teams',
    renderer: getRenderer(MFRepositories.SennTeams, 'page'),
    context: {
      analytics: {
        module: 'sennteams-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'sennteams-mf-component',
      },
    },
  },
  '/customer-data': {
    name: 'customer-data',
    renderer: getRenderer(MFRepositories.CustomerData, 'page'),
    context: {
      analytics: {
        module: 'customer-data-mf-components',
        submodule: '',
      },
      logger: {
        codeOwners: 'pod-portal',
        module: 'customer-data-mf-components',
      },
    },
  },
  '/customer-order/:id': {
    name: 'customer-order',
    renderer: getRenderer(MFRepositories.Shipment, 'shipment'),
    context: {
      analytics: {
        module: 'shipment-mf-component',
        submodule: 'customer-order',
      },
      logger: {
        codeOwners: 'ingestion',
        module: 'shipment',
      },
    },
  },
  '/single-view': {
    name: 'single-view',
    renderer: getRenderer(MFRepositories.SingleView, 'page'),
    context: {
      analytics: {
        module: 'single-view-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'single-view-mf-component',
      },
    },
  },
  '/trips/:id': {
    name: 'trip',
    renderer: getRenderer(MFRepositories.Trip, 'page'),
    context: {
      analytics: {
        module: 'trip-mf',
        submodule: '',
      },
      logger: {
        codeOwners: 'trip',
        module: 'trip-mf',
      },
    },
  },
  '/spotter': {
    name: 'spotter',
    renderer: getRenderer(MFRepositories.Spotter, 'page'),
    context: {
      analytics: {
        module: 'spotter-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'shipper-integrations',
        module: 'spotter-mf-component',
      },
    },
  },
  '/task-allocation': {
    name: 'task-allocation',
    renderer: getRenderer(MFRepositories.TaskAllocation, 'taskallocationmfApp'),
    context: {
      analytics: {
        module: 'task-allocation-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'task-allocation-mf-component',
      },
    },
  },
  '/vetting-dashboard': {
    name: 'vetting-dashboard',
    renderer: getRenderer(MFRepositories.Vetting, 'VettingDashboard'),
    context: {
      analytics: {
        module: 'vetting_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'vetting',
        module: 'vetting_micro_frontends',
      },
    },
  },
  '/vetting/:vettingId': {
    name: 'vetting-review',
    renderer: getRenderer(MFRepositories.Vetting, 'VettingReview'),
    context: {
      analytics: {
        module: 'vetting_micro_frontends',
        submodule: '',
      },
      logger: {
        codeOwners: 'vetting',
        module: 'vetting_micro_frontends',
      },
    },
  },
  '/archive/:orderId': {
    name: 'archived-order',
    renderer: getRenderer(MFRepositories.ArchivedOrderPage, 'page'),
    context: {
      analytics: {
        module: 'archived-order-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'archived-order-mf-component',
      },
    },
  },
  '/tenders': {
    name: 'tenders',
    renderer: getRenderer(MFRepositories.TenderWithSennder, 'page'),
    context: {
      analytics: {
        module: 'mf-tender-with-sennder',
        submodule: '',
      },
      logger: {
        codeOwners: 'carrier-dedication',
        module: 'mf-tender-with-sennder',
      },
    },
  },
  '/tender-opportunities': {
    name: 'opportunities',
    renderer: getRenderer(MFRepositories.Tendering, 'page'),
    context: {
      analytics: {
        module: 'tendering-mf-component',
        submodule: 'opportunities',
      },
      logger: {
        codeOwners: 'tendering',
        module: 'tendering-mf-component',
      },
    },
  },
  '/tender-analytics': {
    name: 'analytics',
    renderer: getRenderer(MFRepositories.Tendering, 'page'),
    context: {
      analytics: {
        module: 'tendering-mf-component',
        submodule: 'analytics',
      },
      logger: {
        codeOwners: 'tendering',
        module: 'tendering-mf-component',
      },
    },
  },

  '/corridors': {
    name: 'corridor-accountability',
    renderer: getRenderer(MFRepositories.CorridorAccountability, 'page'),
    context: {
      analytics: {
        module: 'corridor-accountability-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'ops-compass',
        module: 'corridor-accountability-mf-component',
      },
    },
  },
  '/experiments': {
    name: 'experiments',
    renderer: getRenderer(MFRepositories.Experiments, 'page'),
    context: {
      analytics: {
        module: 'experiments',
        submodule: '',
      },
      logger: {
        codeOwners: 'Cloud Platform Team',
        module: 'experiments',
      },
    },
    guards: [checkExperimentsAccess],
  },
  '/autobidding': {
    name: 'spot-autobidding',
    renderer: getRenderer(MFRepositories.SpotAutobidding, 'page'),
    context: {
      analytics: {
        module: 'spot-autobidding-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'data-and-ml',
        module: 'spot-autobidding-mf-component',
      },
    },
    guards: [checkSpotAutobiddingAccess],
  },
  '/tour-cost': {
    name: 'tour-cost',
    renderer: getRenderer(MFRepositories.TourCost, 'page'),
    context: {
      analytics: {
        module: 'tour-cost-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'data-and-ml',
        module: 'tour-cost-mf-component',
      },
    },
  },
  '/integrations': {
    name: 'integrations',
    renderer: getRenderer(MFRepositories.IntegrationsPlatform, 'page'),
    context: {
      analytics: {
        module: 'integrations-platform-mf-component',
        submodule: '',
      },
      logger: {
        codeOwners: 'shipper-integrations',
        module: 'integrations-platform-mf-component',
      },
    },
    guards: [restrictToDev],
  },
} as const satisfies {
  [TRoute: string]: AppRouteMeta
}
